import { provideHttpClient } from "@angular/common/http";
import {
  inject,
  provideAppInitializer,
  type EnvironmentProviders,
  type Provider,
} from "@angular/core";
import { TranslateService, provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";

import { CASTOFFPRO_LOCALE } from "../storage/storage-keys";

/** Supported UI languages. Keep in sync with files in `public/i18n/`. */
export const SUPPORTED_LOCALES = ["en", "pl"] as const;
export type CastOffProLocale = (typeof SUPPORTED_LOCALES)[number];
const DEFAULT_LOCALE: CastOffProLocale = "pl";

function readStoredLocale(): CastOffProLocale {
  if (typeof localStorage === "undefined") return DEFAULT_LOCALE;
  const raw = localStorage.getItem(CASTOFFPRO_LOCALE);
  if (raw === "en" || raw === "pl") return raw;
  return DEFAULT_LOCALE;
}

function writeStoredLocale(lang: string): void {
  if (typeof localStorage === "undefined") return;
  if (lang === "en" || lang === "pl") {
    localStorage.setItem(CASTOFFPRO_LOCALE, lang);
  }
}

/**
 * Bootstrap providers for `@ngx-translate/core` with a JSON HTTP loader.
 *
 * - Default language: `pl` (overridden by previously stored choice).
 * - Fallback: `en`.
 * - Locale persistence: localStorage key `castoffpro.locale.v1`.
 */
export function provideCastOffProI18n(): (Provider | EnvironmentProviders)[] {
  return [
    provideHttpClient(),
    provideTranslateService({
      defaultLanguage: DEFAULT_LOCALE,
      fallbackLang: "en",
    }),
    provideTranslateHttpLoader({ prefix: "/i18n/", suffix: ".json" }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      translate.use(readStoredLocale());
      translate.onLangChange.subscribe((event) =>
        writeStoredLocale(event.lang),
      );
    }),
  ];
}
